import { DetailPanel, useDetailPanelInteractionValue, type PanelSpan } from '@/components/shared';
import type { BackofficeMessageBusPeekedMessage } from '../types';

const SELECTED_MESSAGE_KEY = 'messagebus:selected-message';

export interface MessageBusMessagePreviewTileProps {
  title: string;
  sourceLabel: string;
  span?: PanelSpan;
  currentSpan?: PanelSpan;
  panelId?: string;
}

function PreviewSection(props: { title: string; value: string | null }) {
  if (!props.value) {
    return null;
  }

  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
        {props.title}
      </h4>
      <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-800">
        {props.value}
      </pre>
    </section>
  );
}

function SummaryRow(props: { label: string; value: string | null }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
        {props.label}
      </dt>
      <dd className="mt-1 break-words text-sm text-neutral-900">{props.value ?? '—'}</dd>
    </div>
  );
}

export function MessageBusMessagePreviewTile({
  title,
  sourceLabel,
  span = '4x3',
  currentSpan,
  panelId,
}: MessageBusMessagePreviewTileProps) {
  const [selectedMessage] =
    useDetailPanelInteractionValue<BackofficeMessageBusPeekedMessage>(SELECTED_MESSAGE_KEY);

  return (
    <DetailPanel
      title={title}
      span={span}
      currentSpan={currentSpan}
      panelId={panelId}
      contentOverflow="auto"
    >
      {!selectedMessage ? (
        <div
          className="py-10 text-center text-body text-neutral-500"
          data-testid="messagebus-message-preview-tile"
        >
          Select a message from the {sourceLabel.toLowerCase()} list to preview it.
        </div>
      ) : (
        <div className="space-y-4" data-testid="messagebus-message-preview-tile">
          <div className="grid gap-3 md:grid-cols-2">
            <SummaryRow label="Message ID" value={selectedMessage.messageId} />
            <SummaryRow label="Sequence number" value={selectedMessage.sequenceNumber} />
            <SummaryRow label="Subject" value={selectedMessage.subject} />
            <SummaryRow label="Source" value={selectedMessage.source} />
            <SummaryRow label="Content type" value={selectedMessage.contentType} />
            <SummaryRow
              label="Enqueued"
              value={
                selectedMessage.enqueuedAt
                  ? new Date(selectedMessage.enqueuedAt).toLocaleString()
                  : null
              }
            />
            <SummaryRow
              label="Delivery count"
              value={
                selectedMessage.deliveryCount === null
                  ? null
                  : String(selectedMessage.deliveryCount)
              }
            />
            <SummaryRow label="Dead-letter reason" value={selectedMessage.deadLetterReason} />
          </div>

          <PreviewSection title="Body" value={selectedMessage.bodyText || '(empty message body)'} />
          {selectedMessage.bodyTruncated ? (
            <p className="text-xs text-neutral-500">Body preview was truncated.</p>
          ) : null}
          <PreviewSection
            title="Application properties"
            value={selectedMessage.applicationPropertiesJson}
          />
          <PreviewSection title="System properties" value={selectedMessage.systemPropertiesJson} />
        </div>
      )}
    </DetailPanel>
  );
}
